import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query getMyOrganization {
    getMyOrganization {
      id
      name
      shortName
      type
      levelId
      parentOrganizationId
      subjectLabel
      level {
        levelAttributeList {
          id
          name
          sortOrder
          isMandatory
          fieldType
          optionList
        }
        childLevelList {
          id
          name
        }
        name
        id
      }
      rootOrganization {
        level {
          id
          name
          childLevelList {
            id
            name
          }
        }
        rootLevelList {
          id
          name
          parentLevelId
          orderNumber
          childLevelList {
            id
            name
          }
        }
        subjectKeyList {
          id
          name
          priorityOrder
          isMandatory
        }
        logoUrl
        subjectLabel
        hasSubjectLogin
        hasSubjectLogin
        hasPopulationCount
        name
        id
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.OrganizationState = {
  result: null,
  error: null,
  loading: false
};

export const getMyOrganizationRequest = createAsyncThunk<
  TYPES.Organization,
  {},
  {extra: ApolloClient<NormalizedCacheObject>;}>(
  "organization/get_my_organization", async (params: {}, { extra: client }) => {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params,
        fetchPolicy: "network-only"
      });
      return response.data.getMyOrganization;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  });

const getMyOrganizationSlice = createSlice({
  name: "get_my_organization",
  initialState: initialState,
  reducers: {
    getMyOrganizationClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getMyOrganizationRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getMyOrganizationRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getMyOrganizationRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: "Failed to fetch",
        statusCode: "500"
      };
      state.loading = false;
    });
  }
});

export const { getMyOrganizationClear } = getMyOrganizationSlice.actions;

export const getMyOrganization = (state: RootState) => ({
  result: state.organization.getMyOrganization.result,
  error: state.organization.getMyOrganization.error,
  loading: state.organization.getMyOrganization.loading
});

export default getMyOrganizationSlice.reducer;